"use client";

import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import {
  ArrowLeftIcon,
  type ArrowLeftIconHandle,
  XIcon,
  type XIconHandle,
} from "@/components/widget/icons/animated";
import { useIconHover } from "@/lib/use-icon-hover";
import { cn } from "@/lib/utils";

interface TabTitleHeaderProps {
  title?: string;
  onBack?: () => void;
  onClose: () => void;
  className?: string;
  /** Optional expand chip — only article views pass this; list tabs
   *  omit it so the bar stays clean. */
  expanded?: boolean;
  onToggleExpand?: () => void;
}

/**
 * Sticky title bar for Help / News list + drill-in (article) views.
 * Back + close icons animate on the **button's** hover, not their own
 * — driven via `useIconHover` so visitors see motion when they hover
 * the tap target.
 *
 * **Layout** is a fixed-height (`h-14`) three-zone flex row:
 *   [ left zone ] [ centred title ] [ right zone ]
 * The left + right zones are both `flex-1`, so they reserve equal
 * width regardless of how many action buttons each holds. That keeps
 * the title dead-centred in the header whether it's a list tab (close
 * only) or an article view (back + expand + close). The title
 * `truncate`s so long article names ellipsise instead of colliding
 * with the edge buttons. Fixed height means every header — list or
 * article — is exactly the same height.
 */
export function TabTitleHeader({
  title,
  onBack,
  onClose,
  className,
  expanded,
  onToggleExpand,
}: TabTitleHeaderProps) {
  const t = useTranslations("header");
  const back = useIconHover<ArrowLeftIconHandle>();
  const close = useIconHover<XIconHandle>();

  return (
    <header
      data-wx-top-bar
      className={cn(
        "flex h-14 items-center gap-2 border-b border-wx-border bg-wx-bg px-2",
        className,
      )}
    >
      {/* Left zone — back affordance (or empty spacer). `flex-1` keeps
          it equal-width to the right zone so the title stays centred. */}
      <div className="flex flex-1 items-center justify-start">
        {onBack && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t("back")}
            onClick={onBack}
            className="shrink-0"
            {...back.parentHandlers}
          >
            <ArrowLeftIcon ref={back.iconRef} size={18} className="rtl:-scale-x-100" />
          </Button>
        )}
      </div>

      {/* Centre — title. `truncate` ellipsises long article names. */}
      {title && (
        <h2 className="min-w-0 shrink truncate text-center text-base font-semibold text-wx-fg">
          {title}
        </h2>
      )}

      {/* Right zone — expand + close. `flex-1 justify-end` mirrors the
          left zone's width so the centre title stays header-centred. */}
      <div className="flex flex-1 items-center justify-end gap-0">
        {onToggleExpand && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={expanded ? t("collapse") : t("expand")}
            onClick={onToggleExpand}
          >
            {expanded ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="4 14 10 14 10 20" />
                <polyline points="20 10 14 10 14 4" />
                <line x1="14" y1="10" x2="21" y2="3" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="15 3 21 3 21 9" />
                <polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            )}
          </Button>
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t("close")}
          onClick={onClose}
          {...close.parentHandlers}
        >
          <XIcon ref={close.iconRef} size={18} />
        </Button>
      </div>
    </header>
  );
}
